import { StoreModule } from "@/constants/store.ts";
import {
  SubscriptionBundle,
  SubscriptionModule,
  SubscriptionsFilterType,
  SubscriptionType,
  WorkspaceLocale,
  WorkspaceType,
} from "@/constants/workplaces.ts";
import {
  type Branding,
  createAbortController,
  NotificationsLevel,
  type WorkspaceBillingFields,
  type WorkspaceLimits,
} from "@coronet/cui";
import { defineStore } from "pinia";
import { type BaseMspWorkspaceFilters, useFiltersStore } from "@/_store/filters.module.ts";
import { AxiosError, type AxiosRequestConfig } from "axios";
import { axiosInstance } from "@/plugins/https.ts";
import api from "@/_helpers/api.ts";
import { i18n } from "@/plugins/i18n.ts";
import { type WorkspaceListItem } from "@/_store/workspaces-list.module.ts";
import type { DialogDataConfig } from "@/_store/dialogs.module.ts";
import type { ApplyGlobalPolicyData } from "@/_store/msp/global-policies/msp-global-policies.module.ts";
import type { Paged, Pagination } from "@/types.ts";
import type { MspLabel } from "@/_store/msp/msp-labels.module.ts";
import isEmpty from "lodash/isEmpty";
import { useSnackbarStore } from "@/_store/snackbar.module.ts";
import { type ModulesSettings, useWorkspaceStore } from "@/_store/workspace.module.ts";
import { useAccountStore } from "@/_store/account.module.ts";
import type { ManageWorkspacesContext } from "@/constants/msp.ts";
import {
  applyMspGlobalPolicy,
  archiveMspWorkspace,
  bulkEditWorkspaceLabels,
  convertMspWorkspace,
  createMspWorkspace,
  editMspSubscription,
  editMspWorkspace,
  extendMspWorkspaceTrial,
  unarchiveMspWorkspace,
  updateMspSubscriptionType,
} from "@/_helpers/msp-store-actions.ts";
let abortController: AbortController | undefined;

export interface SubscriptionRulesConfig {
  allowedBundles?: SubscriptionBundle[];
  allowedModules: SubscriptionModule[];
}

export interface MspWorkspaceDialogItem extends MspWorkspaceDetails {
  maxProtectedUsers?: number;
  image?: File;
  emailImage?: File;
  parentWorkspaceId?: string;
  endDate?: number;
  adminEmail?: string;
  mspPolicyId?: string;
  allowPolicyOverriding?: boolean;
  subscriptionRules?: SubscriptionRulesConfig;
  context: ManageWorkspacesContext;
}

export interface MspHierarchyItem {
  workspaceId: string;
  workspaceName: string;
}

export interface BaseMspWorkspaceListItem {
  name: string;
  displayName?: string;
  workspaceId: string;
  type: WorkspaceType;
  protectedUsers: number;
  protectableUsers: number;
  mfaRequired: boolean;
  protectedDevices: number;
  subscription: Subscription;
  subscriptionType: SubscriptionType;
  since: number;
  creationTime: number;
  canBeArchived: boolean;
  parentName?: string;
  ticketsCount: number;
  trialEndDate?: number;
  trialAvailable?: boolean;
  limited?: boolean;
  /** `null` once the workspace was switched back to unlimited in the create/edit form. */
  limits?: WorkspaceLimits | null;
}

export interface MspWorkspaceListItem extends BaseMspWorkspaceListItem {
  labels: MspLabel[];
  mspPolicyName: string;
}

export interface GlobalMspWorkspaceListItem extends BaseMspWorkspaceListItem {
  labels?: never;
  mspPolicyName?: never;
}

// Shared workspace types and enums live in @coronet/cui. Re-exported here so existing
// `@/_store/msp/msp.module` imports keep working unchanged.
export { NotificationsLevel };

export interface MspWorkspaceDetails extends MspWorkspaceListItem, WorkspaceBillingFields {
  assignedAdmins: AssignedAdmin[];
  domain: string;
  companyName: string;
  notificationsLevel: NotificationsLevel;
  lastActivity?: number;
  supportEnabled: boolean;
  psaEnabled?: boolean;
  scanUnprotectedUsers: boolean;
  parentScanUnprotectedUsers: boolean;
  branding: Branding;
  showDisabledModules: boolean;
  isCoroEmployeeWorkspace?: boolean;
  parentDomain?: string;
  parentPsaEnabled?: boolean;
  languageCode: WorkspaceLocale;
}

export interface AssignedAdmin {
  roleId: RoleID;
  email: string;
}

export interface RoleID {
  timestamp: number;
  date: Date;
}

export interface Subscription {
  modules: ModulesSettings;
  bundles: SubscriptionBundle[];
}

export interface BaseMspWorkspacesModuleState<T extends BaseMspWorkspaceListItem> {
  pagination: Pagination;
  pages: number;
  total: number;
  workspaces: Array<T>;
  showSkeletonLoader: boolean;
  createWorkspaceInProgress: boolean;
  createWorkspaceCompleted: boolean;
  createWorkspaceFailed: boolean;
  loading: boolean;
}

type MspModuleState = BaseMspWorkspacesModuleState<MspWorkspaceListItem>;

export type ParentWorkspaceSearchPermission =
  | "ADD_CHANNEL_ADMINS"
  | "ADD_CHANNEL_ROLES"
  | "CREATE_CHILD_WORKSPACE"
  | "CREATE_CHANNEL_WORKSPACE";

const defaultState: MspModuleState = {
  pagination: {
    page: 0,
    pageSize: 25,
  },
  pages: 0,
  total: 0,
  workspaces: [],
  showSkeletonLoader: false,
  createWorkspaceInProgress: false,
  createWorkspaceCompleted: false,
  createWorkspaceFailed: false,
  loading: false,
};

export interface EditSubscriptionData extends Subscription {
  workspaceIds: string[];
  modulesRemovedDuringEdit: Array<SubscriptionModule>;
}

export const useMspStore = defineStore(StoreModule.MSP, {
  state: () => ({ ...defaultState }),
  actions: {
    resetCreateWorkspaceLoaders() {
      this.createWorkspaceInProgress = false;
      this.createWorkspaceFailed = false;
      this.createWorkspaceCompleted = false;
    },
    setPagination(pagination: Pagination) {
      this.pagination = { ...pagination };
    },
    resetPagination() {
      this.pagination = { ...defaultState.pagination };
    },
    async getMspWorkspaces(
      payload: { showSkeletonLoader?: boolean; triggeredByFilters?: boolean } = {
        showSkeletonLoader: false,
        triggeredByFilters: false,
      }
    ) {
      abortController = createAbortController(abortController);
      this.showSkeletonLoader = payload.showSkeletonLoader ?? false;
      this.loading = true;

      if (payload.triggeredByFilters) {
        this.pagination = { page: 0, pageSize: 25 };
      }
      const filtersStore = useFiltersStore();
      const {
        addons,
        types,
        subscriptionTypes,
        modules,
        bundles,
        search,
        labels,
        page,
        pageSize,
        nearExpirationOnly,
        mspPolicyId,
        userProtectionLimit,
      } = {
        ...getMspFilters(filtersStore.filters.mspFilters),
        ...this.pagination,
      };
      const request: AxiosRequestConfig = {
        ...api.mspWorkspaces(),
        params: {
          addons: addons.join(","),
          types: types?.join(","),
          subscriptionTypes: subscriptionTypes.join(","),
          nearExpirationOnly,
          modules: modules.join(","),
          bundles: bundles.join(","),
          labels: labels?.join(","),
          search,
          page,
          pageSize,
          mspPolicyId,
          userProtectionLimit,
        },
      };

      try {
        const { data } = await axiosInstance.request({
          ...request,
          signal: abortController.signal,
        });

        this.workspaces = data.items;
        this.total = data.total;
        this.showSkeletonLoader = false;
        this.loading = false;
      } catch (error) {
        if (error && (error as AxiosError).code === AxiosError.ERR_CANCELED) {
          return;
        }
        console.error(error);
        this.showSkeletonLoader = false;
        this.loading = false;
      }
      abortController = undefined;
    },
    async createWorkspace(payload: DialogDataConfig<MspWorkspaceDialogItem>) {
      this.createWorkspaceInProgress = true;
      this.createWorkspaceCompleted = false;
      this.createWorkspaceFailed = false;

      try {
        await createMspWorkspace(payload.item);
        this.createWorkspaceInProgress = false;
        this.createWorkspaceCompleted = true;
      } catch (error) {
        this.createWorkspaceInProgress = false;
        this.createWorkspaceFailed = true;
        console.error(error);
      }
    },
    async editWorkspace(payload: DialogDataConfig<MspWorkspaceDialogItem>) {
      try {
        await editMspWorkspace(payload.item, async () => {
          await this.getMspWorkspaces();
        });
      } catch (error) {
        console.error(error);
      }
    },
    async deleteWorkspace(payload: DialogDataConfig<MspWorkspaceDialogItem>) {
      const request = {
        ...api.mspWorkspace(payload.item.workspaceId),
        method: "DELETE",
      };

      try {
        await axiosInstance.request(request);
        await this.getMspWorkspaces();
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.manageWorkspaces.workspaceDeleted")
        );
      } catch (error) {
        console.error(error);
      }
    },
    async convertWorkspace(
      payload: DialogDataConfig<MspWorkspaceDialogItem & { descendantConversionOption?: string }>
    ) {
      try {
        await convertMspWorkspace(payload, async () => {
          await this.getMspWorkspaces();
        });
      } catch (error) {
        console.error(error);
      }
    },
    async unarchiveWorkspace(payload: DialogDataConfig<MspWorkspaceDialogItem>) {
      try {
        await unarchiveMspWorkspace(payload.item.workspaceId, async () => {
          await this.getMspWorkspaces();
        });
      } catch (error) {
        console.error(error);
      }
    },
    async archiveWorkspace(payload: DialogDataConfig<MspWorkspaceDialogItem>) {
      try {
        await archiveMspWorkspace(payload.item.workspaceId, async () => {
          await this.getMspWorkspaces();
        });
      } catch (error) {
        console.error(error);
      }
    },
    async editSubscription(payload: EditSubscriptionData) {
      try {
        await editMspSubscription(payload, async () => {
          await this.getMspWorkspaces();
        });
      } catch (error) {
        console.error(error);
      }
    },
    async bulkEditLabels(payload: { labelIds: string[]; workspaceIds: string[] }) {
      try {
        await bulkEditWorkspaceLabels(payload, async () => {
          await this.getMspWorkspaces();
        });
      } catch (e) {
        console.error(e);
      }
    },
    async stopSubscription(payload: DialogDataConfig<string[]>) {
      const data = {
        workspaceIds: payload.item,
        subscriptionType: SubscriptionType.FREEZE,
      };
      try {
        await updateMspSubscriptionType(data, async () => {
          await this.getMspWorkspaces();
        });
      } catch (error) {
        console.error(error);
      }
    },
    async startSubscription(payload: DialogDataConfig<string[]>) {
      const data = {
        workspaceIds: payload.item,
        subscriptionType: SubscriptionType.SUBSCRIPTION,
      };
      try {
        await updateMspSubscriptionType(data, async () => {
          await this.getMspWorkspaces();
        });
      } catch (error) {
        console.error(error);
      }
    },
    async extendTrial(payload: DialogDataConfig<MspWorkspaceDialogItem>) {
      try {
        await extendMspWorkspaceTrial(payload, async () => {
          await this.getMspWorkspaces();
        });
      } catch (error) {
        console.error(error);
      }
    },
    async checkInvitedAdminAlreadyMspAdmin(data: {
      parentWorkspaceId: string;
      invitedAdmin: string;
    }): Promise<boolean> {
      const request = {
        ...api.checkInvitedAdminAlreadyMspAdmin(),
        data,
      };

      try {
        const { data } = await axiosInstance.request(request);
        return data;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async getRelevantHierarchy() {
      const workspaceStore = useWorkspaceStore();
      const request = {
        ...api.getRelevantHierarchy(),
      };

      try {
        const { data } = await axiosInstance.request<MspHierarchyItem>(request);
        useAccountStore().$patch({ hasMspAccess: !isEmpty(data) });
        await workspaceStore.updateWorkspaceAndPermissions(data.workspaceId, true);
        return data;
      } catch (error) {
        // Named on purpose: `rumBeforeSend` drops the /msp/hierarchy 403 network event for
        // the `checkMspAccess` probe and cannot tell this caller apart, so this log is the
        // surviving signal — and a distinct message keeps it out of the generic
        // "Request failed with status code" cluster. See PLAT-4382.
        console.error("Failed to load the relevant MSP hierarchy", error);
        return null;
      }
    },
    async getHierarchies(
      payload: { search: string; pagination: Pagination } = {
        search: "",
        pagination: { page: 0, pageSize: 10 },
      }
    ) {
      const request = {
        ...api.getHierarchies(payload.search, payload.pagination.page, payload.pagination.pageSize),
      };

      return axiosInstance.request(request);
    },
    async getMspWorkspaceDescendants() {
      const request = {
        ...api.getMspWorkspaceDescendants(),
      };

      return axiosInstance.request(request);
    },
    async getWorkspaceCandidates(
      search = "",
      permission: ParentWorkspaceSearchPermission,
      pagination: Pagination
    ): Promise<Paged<{ workspaceName: string; workspaceId: string }>> {
      const request = {
        ...api.workspaceMspCandidates(),
        params: {
          search,
          permission,
          ...pagination,
        },
      };

      try {
        const { data } = await axiosInstance.request(request);
        return { items: data, total: data.length };
      } catch (error) {
        console.error(error);
        return { items: [], total: 0 };
      }
    },
    async searchMspConversionWorkplaceCandidates(query: {
      name: string;
      type?: WorkspaceType;
      channelWorkspaceIdToExclude?: string;
    }): Promise<Paged<WorkspaceListItem> | undefined> {
      const request = {
        ...api.mspConversionWorkplaceCandidates({ ...query, page: 0, pageSize: 25 }),
      };
      try {
        const { data } = await axiosInstance.request(request);
        return data;
      } catch (e) {
        console.error(e);
      }
    },
    async getMspWorkspaceDetails(workspaceId: string): Promise<MspWorkspaceDetails | undefined> {
      const request = {
        ...api.mspWorkspaceDetails(workspaceId),
      };

      try {
        const { data } = await axiosInstance.request(request);
        return data;
      } catch (error) {
        console.error(error);
      }
    },
    async applyGlobalPolicy(payload: ApplyGlobalPolicyData) {
      try {
        await applyMspGlobalPolicy(payload, async () => {
          await this.getMspWorkspaces();
        });
      } catch (e) {
        console.error(e);
      }
    },
    async getWorkspaceSubscriptionRules(workspaceId: string): Promise<SubscriptionRulesConfig> {
      const request = {
        ...api.workspaceSubscriptionRules(workspaceId),
      };

      try {
        const { data } = await axiosInstance.request(request);
        return {
          ...data,
          allowedBundles: data.allowedBundles ?? [],
        };
      } catch (error) {
        console.error(error);
        return {
          allowedBundles: [],
          allowedModules: [],
        };
      }
    },
  },
});

/**
 * Transforms filter state into API query parameters for server-side workspace fetching.
 * Splits the `addons` array by type (addon/module/bundle) into separate params.
 * Accepts BaseMspWorkspaceFilters with optional view-specific fields (types, labels, mspPolicyId).
 */
export function getMspFilters(
  filters: BaseMspWorkspaceFilters & {
    types?: WorkspaceType[];
    labels?: string[];
    mspPolicyId?: string;
    userProtectionLimit?: string | null;
  }
) {
  const { addons, search, subscriptionTypes, nearExpirationOnly } = filters;

  return {
    subscriptionTypes,
    types: filters.types,
    search,
    labels: filters.labels,
    nearExpirationOnly,
    mspPolicyId: filters.mspPolicyId,
    userProtectionLimit: filters.userProtectionLimit,
    addons: addons.filter((v) => v.type === SubscriptionsFilterType.ADDON).map((v) => v.name),
    modules: addons.filter((v) => v.type === SubscriptionsFilterType.MODULE).map((v) => v.name),
    bundles: addons.filter((v) => v.type === SubscriptionsFilterType.BUNDLE).map((v) => v.name),
  };
}
